import { LetterRule, personKey, ReadingMode, RULE_PRESETS } from './initials';

/**
 * Game flow for "İnisiallar". Two sides (players or teams) take turns naming
 * people that fit the round's letters, each within the turn time:
 * - a named person is first checked by the system (letters, repeats, a short
 *   "who is this"), then the opponent accepts or disputes it;
 * - whoever fails a turn (time runs out or they give up) hands it over: if the
 *   other side had just named someone, the other side wins the round; if the
 *   failing side opened the round, the other side gets one turn to win it; if
 *   both sides fail in a row, the round is a draw.
 */

export type SideId = 'a' | 'b';

export const otherSide = (side: SideId): SideId => (side === 'a' ? 'b' : 'a');

export interface Side {
  name: string;
  /** Optional team members, shown under the side's name. */
  members: string;
}

export interface GameSettings {
  sides: Record<SideId, Side>;
  turnSeconds: number;
  mode: ReadingMode;
  /** A fixed rule, or `random` to draw one of the presets every round. */
  rule: LetterRule | 'random';
  /** `auto`: the platform draws the letters; `manual`: the sides pick them. */
  letterSource: 'auto' | 'manual';
  /** Round wins needed to take the match; 0 plays until stopped. */
  targetWins: number;
}

export interface Answer {
  id: string;
  side: SideId;
  fullName: string;
  /** Original spelling, when the name is written by pronunciation. */
  original: string;
  description: string;
  nameIdx: number;
  surnameIdx: number;
}

export type RoundEvent =
  | { kind: 'accepted'; answer: Answer }
  | { kind: 'rejected'; answer: Answer; reason: string }
  | { kind: 'failed'; side: SideId; reason: 'timeout' | 'gaveUp' };

export interface Round {
  no: number;
  rule: LetterRule;
  letters: { name: string; surname: string };
  /** Who picked each letter; null when the platform drew them. */
  pickedBy: { name: SideId; surname: SideId } | null;
  starter: SideId;
  events: RoundEvent[];
  winner: SideId | 'draw' | null;
}

export type Phase = 'setup' | 'pick' | 'turn' | 'review' | 'roundOver' | 'matchOver';

export interface TimerState {
  /** Epoch ms when the running turn ends; null while paused. */
  endsAt: number | null;
  /** Time left when paused. */
  remainingMs: number;
}

export interface GameState {
  settings: GameSettings;
  phase: Phase;
  rounds: Round[];
  turn: SideId;
  /** The answer the opponent is reviewing. */
  pending: Answer | null;
  timer: TimerState;
  wins: Record<SideId, number>;
  draws: number;
  /** Snapshots for undo, newest last. */
  past: Omit<GameState, 'past'>[];
}

export const DEFAULT_SETTINGS: GameSettings = {
  sides: { a: { name: '', members: '' }, b: { name: '', members: '' } },
  turnSeconds: 60,
  mode: 'sound',
  rule: RULE_PRESETS[0].rule,
  letterSource: 'auto',
  targetWins: 5,
};

export const createGameState = (settings: GameSettings = DEFAULT_SETTINGS): GameState => ({
  settings,
  phase: 'setup',
  rounds: [],
  turn: 'a',
  pending: null,
  timer: { endsAt: null, remainingMs: settings.turnSeconds * 1000 },
  wins: { a: 0, b: 0 },
  draws: 0,
  past: [],
});

export const sideName = (state: GameState, side: SideId) =>
  state.settings.sides[side].name.trim() || (side === 'a' ? 'Birinci tərəf' : 'İkinci tərəf');

export const currentRound = (state: GameState): Round | null => state.rounds[state.rounds.length - 1] ?? null;

/** Side that opens the next round: the sides take turns starting. */
export const nextStarter = (state: GameState): SideId => {
  const last = currentRound(state);
  return last ? otherSide(last.starter) : 'a';
};

/** People already named (and accepted) anywhere in the match. */
export const usedPersonKeys = (state: GameState) =>
  state.rounds.flatMap(r => r.events.filter(e => e.kind === 'accepted').map(e => personKey((e as { answer: Answer }).answer.fullName)));

export const remainingMs = (timer: TimerState, now: number) =>
  Math.max(0, timer.endsAt === null ? timer.remainingMs : timer.endsAt - now);

export type GameAction =
  | { type: 'startMatch'; settings: GameSettings }
  | { type: 'startRound'; rule: LetterRule; letters: { name: string; surname: string }; pickedBy: Round['pickedBy']; now: number }
  | { type: 'submit'; answer: Answer; now: number }
  | { type: 'review'; accept: boolean; reason?: string; now: number }
  /** `round` guards against a stale timeout firing after the turn already moved on. */
  | { type: 'fail'; side: SideId; round: number; reason: 'timeout' | 'gaveUp'; now: number }
  | { type: 'pause'; now: number }
  | { type: 'resume'; now: number }
  | { type: 'nextRound' }
  | { type: 'finishMatch' }
  | { type: 'newMatch' }
  | { type: 'undo' };

const MAX_PAST = 100;

const freshTimer = (state: GameState, now: number): TimerState => ({
  endsAt: now + state.settings.turnSeconds * 1000,
  remainingMs: state.settings.turnSeconds * 1000,
});

const pausedTimer = (timer: TimerState, now: number): TimerState => ({ endsAt: null, remainingMs: remainingMs(timer, now) });

const withRoundEvent = (state: GameState, event: RoundEvent, winner: Round['winner'] = null): Round[] =>
  state.rounds.map((r, i) => (i === state.rounds.length - 1 ? { ...r, events: [...r.events, event], winner } : r));

/** Closes the round and decides whether the match is over too. */
const finishRound = (state: GameState, rounds: Round[], winner: SideId | 'draw'): GameState => {
  const wins = winner === 'draw' ? state.wins : { ...state.wins, [winner]: state.wins[winner] + 1 };
  const target = state.settings.targetWins;
  const matchOver = target > 0 && winner !== 'draw' && wins[winner] >= target;
  return {
    ...state,
    rounds,
    wins,
    draws: winner === 'draw' ? state.draws + 1 : state.draws,
    pending: null,
    timer: { endsAt: null, remainingMs: 0 },
    phase: matchOver ? 'matchOver' : 'roundOver',
  };
};

/** Last event that decides anything; disputed answers do not count. */
const lastDecisive = (round: Round) =>
  [...round.events].reverse().find(e => e.kind !== 'rejected') as Exclude<RoundEvent, { kind: 'rejected' }> | undefined;

const step = (state: GameState, action: GameAction): GameState => {
  const round = currentRound(state);
  switch (action.type) {
    case 'startMatch':
      return { ...createGameState(action.settings), phase: 'pick' };

    case 'startRound': {
      if (state.phase !== 'pick') return state;
      const starter = nextStarter(state);
      const newRound: Round = {
        no: state.rounds.length + 1,
        rule: action.rule,
        letters: action.letters,
        pickedBy: action.pickedBy,
        starter,
        events: [],
        winner: null,
      };
      return { ...state, rounds: [...state.rounds, newRound], turn: starter, pending: null, phase: 'turn', timer: freshTimer(state, action.now) };
    }

    case 'submit':
      if (state.phase !== 'turn' || action.answer.side !== state.turn) return state;
      return { ...state, pending: action.answer, phase: 'review', timer: pausedTimer(state.timer, action.now) };

    case 'review': {
      if (state.phase !== 'review' || !state.pending || !round) return state;
      const answer = state.pending;
      if (!action.accept) {
        // Disputed: the answering side keeps its turn with the time it had left.
        const rounds = withRoundEvent(state, { kind: 'rejected', answer, reason: action.reason?.trim() ?? '' });
        return {
          ...state,
          rounds,
          pending: null,
          phase: 'turn',
          timer: { endsAt: action.now + state.timer.remainingMs, remainingMs: state.timer.remainingMs },
        };
      }
      const before = lastDecisive(round);
      const event: RoundEvent = { kind: 'accepted', answer };
      // Found someone right after the opponent failed: that wins the round.
      if (before?.kind === 'failed' && before.side !== answer.side) {
        return finishRound(state, withRoundEvent(state, event, answer.side), answer.side);
      }
      return {
        ...state,
        rounds: withRoundEvent(state, event),
        pending: null,
        turn: otherSide(answer.side),
        phase: 'turn',
        timer: freshTimer(state, action.now),
      };
    }

    case 'fail': {
      if (state.phase !== 'turn' || !round || round.no !== action.round || action.side !== state.turn) return state;
      const event: RoundEvent = { kind: 'failed', side: action.side, reason: action.reason };
      const before = lastDecisive(round);
      const opponent = otherSide(action.side);
      if (before?.kind === 'accepted') return finishRound(state, withRoundEvent(state, event, opponent), opponent);
      if (before?.kind === 'failed') return finishRound(state, withRoundEvent(state, event, 'draw'), 'draw');
      // The round's opener failed: the opponent gets one turn to win it.
      return { ...state, rounds: withRoundEvent(state, event), turn: opponent, timer: freshTimer(state, action.now) };
    }

    case 'pause':
      if (state.phase !== 'turn' || state.timer.endsAt === null) return state;
      return { ...state, timer: pausedTimer(state.timer, action.now) };

    case 'resume':
      if (state.phase !== 'turn' || state.timer.endsAt !== null) return state;
      return { ...state, timer: { ...state.timer, endsAt: action.now + state.timer.remainingMs } };

    case 'nextRound':
      if (state.phase !== 'roundOver') return state;
      return { ...state, phase: 'pick' };

    case 'finishMatch':
      if (state.phase === 'setup' || state.phase === 'matchOver') return state;
      return { ...state, pending: null, timer: { endsAt: null, remainingMs: 0 }, phase: 'matchOver' };

    case 'newMatch':
      return createGameState(state.settings);

    default:
      return state;
  }
};

const snapshot = ({ past, ...rest }: GameState): Omit<GameState, 'past'> => rest;

/**
 * Reducer with undo. Undo restores the previous state as it was, except that a
 * running timer comes back paused so nobody loses time to the correction.
 */
export const gameReducer = (state: GameState, action: GameAction): GameState => {
  if (action.type === 'undo') {
    const prev = state.past[state.past.length - 1];
    if (!prev) return state;
    const timer = prev.timer.endsAt === null ? prev.timer : { endsAt: null, remainingMs: prev.timer.remainingMs };
    return { ...prev, timer, past: state.past.slice(0, -1) };
  }
  const next = step(state, action);
  if (next === state) return state;
  // Pausing and resuming are not worth an undo step.
  if (action.type === 'pause' || action.type === 'resume') return { ...next, past: state.past };
  if (action.type === 'startMatch' || action.type === 'newMatch') return { ...next, past: [] };
  return { ...next, past: [...state.past, snapshot(state)].slice(-MAX_PAST) };
};
